import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ReadingGroup } from 'shared/models';
import { AlertService, ToastService } from 'shared/ui';
import { ReadingGroupExitDialogComponent } from '../../dialogs/reading-group-exit-dialog/reading-group-exit-dialog.component';
import { Clipboard } from '@angular/cdk/clipboard';
import { BookReadingGroupsAPIService } from 'shared/services';

interface ReadingGroupListItemView {
  id: ReadingGroup['id'];
  description: ReadingGroup['description'];
  name: ReadingGroup['name'];
  type: ReadingGroup['type'];
  member_count: ReadingGroup['member_count'];
  max_member_count: ReadingGroup['max_member_count'];
  highlight_count: ReadingGroup['highlight_count'];
  memo_count: ReadingGroup['memo_count'];
  leader_nickname: ReadingGroup['leader_nickname'];
  status: ReadingGroup['status'];
  is_hidden: ReadingGroup['is_hidden'];
  is_liked: ReadingGroup['is_liked'];
  content: {
    id: string;
    title: string;
    author: string;
    image_url: string;
  };
}

function createInvitationTextHTML(
  name: string,
  contentId: string,
  id: number
): string {
  return `북이오에서 ‘${name}’ 그룹책을 함께읽어요. 함께읽기는 책 구매 후 바로 가능합니다!
${location.origin}/@${contentId}?invitedGroupId=${id}`;
}

@Component({
  selector: 'lib-reading-group-list-item',
  templateUrl: './reading-group-list-item.component.html',
  styleUrl: './reading-group-list-item.component.scss',
})
export class ReadingGroupListItemComponent implements OnInit {
  @ViewChild('moreMenu', { read: ElementRef })
  moreMenuElementRef?: ElementRef<HTMLElement>;

  @ViewChild('moreButton', { read: ElementRef })
  moreButtonElementRef?: ElementRef<HTMLElement>;

  @ViewChild('namePopover', { read: ElementRef })
  namePopoverElementRef?: ElementRef<HTMLElement>;

  @ViewChild('nameText', { read: ElementRef })
  nameTextElementRef?: ElementRef<HTMLElement>;

  @Input() group!: ReadingGroupListItemView;
  @Input() invitedGroupId?: number; // 초대받은 그룹 ID

  @Output() groupDetailsButtonClick =
    new EventEmitter<ReadingGroupListItemView>();
  @Output() groupExited = new EventEmitter<void>();
  @Output() likeStateChanged = new EventEmitter<void>();

  public isMoreMenuOpen = false;
  public isNameHovered = false;
  public _invitationText!: string;
  public isLiked = false;

  constructor(
    private _alertService: AlertService,
    private _clipboard: Clipboard,
    private _toastService: ToastService,
    private _bookReadingGroupsAPIService: BookReadingGroupsAPIService
  ) {}

  ngOnInit(): void {
    this._invitationText = createInvitationTextHTML(
      this.group.name,
      this.group.content.id,
      this.group.id
    );
    this.isLiked = this.group.is_liked === 1;
  }

  updatePopoverPosition(): void {
    if (
      !this.isNameHovered ||
      !this.namePopoverElementRef ||
      !this.nameTextElementRef
    ) {
      return;
    }

    const nameTextElement = this.nameTextElementRef.nativeElement;
    const popoverElement = this.namePopoverElementRef.nativeElement;

    // dialog 내부에 있는지 확인하고 위치 계산
    const dialogElement = nameTextElement.closest(
      'sui-dialog, .cdk-overlay-pane, [role="dialog"]'
    );

    if (!dialogElement) {
      return;
    }

    const dialogRect = dialogElement.getBoundingClientRect();
    const elementRect = nameTextElement.getBoundingClientRect();

    // dialog 내에서의 상대적 위치
    const relativeLeft = elementRect.left - dialogRect.left;
    const relativeTop = elementRect.top - dialogRect.top;

    // 모바일 앱인지 확인
    const isMobileApp = /BukHybridApp\//.test(navigator.userAgent);

    if (window.innerWidth > 1200) {
      popoverElement.style.left = `${relativeLeft}px`;
      popoverElement.style.top = `${relativeTop + 18}px`;
    } else if (window.innerWidth > 768) {
      popoverElement.style.left = `${relativeLeft}px`;
      popoverElement.style.top = `${relativeTop + 52}px`;
    } else {
      // 모바일 앱에서는 제목 높이 + 여유 공간, 웹에서는 기존 값
      const topOffset = isMobileApp ? 70 : 26;
      popoverElement.style.left = `${relativeLeft}px`;
      popoverElement.style.top = `${relativeTop + topOffset}px`;
    }
  }

  onMoreClick(): void {
    this.isMoreMenuOpen = !this.isMoreMenuOpen;
  }

  onNameHover(isHovered: boolean): void {
    // 모바일에서는 호버 이벤트 무시
    if (window.innerWidth <= 768) {
      return;
    }
    this.isNameHovered = isHovered;
    if (isHovered) {
      setTimeout(() => this.updatePopoverPosition(), 0);
    }
  }

  onNameTouch(event: TouchEvent): void {
    // 모바일에서만 터치 이벤트 처리
    if (window.innerWidth <= 768) {
      const target = event.target as HTMLElement;
      // popover 내부를 클릭한 경우는 토글하지 않음
      if (this.namePopoverElementRef?.nativeElement.contains(target)) {
        return;
      }

      const clickedNameText = target.closest('.name-text');
      const currentNameElement =
        this.nameTextElementRef?.nativeElement.closest('.name');
      const clickedNameElement = clickedNameText?.closest('.name');

      // 다른 name-text를 터치한 경우, 기존 popover를 먼저 닫기 위해
      // document에 이벤트를 발생시켜 다른 컴포넌트의 onDocumentClick이 실행되도록 함
      if (
        clickedNameText &&
        clickedNameElement !== currentNameElement &&
        this.isNameHovered
      ) {
        // 다른 제목을 터치했고 현재 popover가 열려있으면 먼저 닫기
        this.isNameHovered = false;
        // 약간의 지연 후 새 제목 열기 (이벤트 버블링이 완료되도록)
        setTimeout(() => {
          if (clickedNameElement === target.closest('.name')) {
            this.isNameHovered = true;
            setTimeout(() => this.updatePopoverPosition(), 0);
          }
        }, 0);
        return;
      }

      this.isNameHovered = !this.isNameHovered;
      if (this.isNameHovered) {
        setTimeout(() => this.updatePopoverPosition(), 0);
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isMobile = window.innerWidth <= 768;

    // 모바일에서 popover 외부 클릭 시 닫기
    if (isMobile && this.isNameHovered && this.namePopoverElementRef) {
      const popoverElement = this.namePopoverElementRef.nativeElement;
      const currentNameElement =
        this.nameTextElementRef?.nativeElement.closest('.name');
      const clickedNameElement = target.closest('.name');
      const clickedNameText = target.closest('.name-text');

      // 다른 name-text를 클릭한 경우 (다른 popover를 열려는 경우)
      if (clickedNameText && clickedNameElement !== currentNameElement) {
        this.isNameHovered = false;
      }
      // popover 외부이고 현재 name 요소도 아닌 경우에만 닫기
      else if (
        !popoverElement.contains(target) &&
        clickedNameElement !== currentNameElement
      ) {
        this.isNameHovered = false;
      }
    }

    if (
      !this.isMoreMenuOpen ||
      !this.moreMenuElementRef ||
      !this.moreButtonElementRef
    ) {
      return;
    }

    const menuElement = this.moreMenuElementRef.nativeElement;
    const moreButtonElement = this.moreButtonElementRef.nativeElement;

    // 클릭된 요소가 메뉴 내부인지 확인
    const clickedInsideMenu = menuElement.contains(target);
    const clickedInsideMoreButton = moreButtonElement.contains(target);

    // 메뉴 내부가 아니면 메뉴 닫기
    if (!clickedInsideMenu && !clickedInsideMoreButton) {
      this.isMoreMenuOpen = false;
    }
  }

  @HostListener('document:touchstart', ['$event'])
  onDocumentTouchStart(event: TouchEvent): void {
    if (
      window.innerWidth <= 768 &&
      this.isNameHovered &&
      this.namePopoverElementRef
    ) {
      const target = event.target as HTMLElement;
      const popoverElement = this.namePopoverElementRef.nativeElement;
      const currentNameElement =
        this.nameTextElementRef?.nativeElement.closest('.name');
      const clickedNameElement = target.closest('.name');
      const clickedNameText = target.closest('.name-text');

      // 다른 name-text를 터치한 경우
      if (clickedNameText && clickedNameElement !== currentNameElement) {
        this.isNameHovered = false;
      }
      // popover 외부이고 현재 name 요소도 아닌 경우에만 닫기
      else if (
        !popoverElement.contains(target) &&
        clickedNameElement !== currentNameElement
      ) {
        this.isNameHovered = false;
      }
    }
  }

  _onGroupDetailsButtonClick(): void {
    this.groupDetailsButtonClick.emit(this.group);
  }

  _onExitGroupButtonClick(): void {
    this._alertService
      .open(ReadingGroupExitDialogComponent, {
        data: {
          groupId: this.group.id,
          status: this.group.status,
        },
      })
      .afterClosed()
      .subscribe((exited) => {
        exited && this.groupExited.emit();

        this.group.member_count--;
      });
  }

  _onCopyInvitationTextButtonClick(event: Event): void {
    event.stopPropagation();
    const text = this._invitationText;
    text && this._clipboard.copy(text);
    this._toastService.open('복사가 완료되었습니다.');
  }

  onClickFavoriteButton(favoriteState: boolean): void {
    if (favoriteState) {
      this._bookReadingGroupsAPIService.addLike(this.group.id).subscribe({
        next: () => {
          this._toastService.open('즐겨읽기 되었습니다.');
          this.isLiked = true;
        },
      });
    } else {
      this._bookReadingGroupsAPIService.removeLike(this.group.id).subscribe({
        next: () => {
          this._toastService.open('즐겨읽기 해제되었습니다.');
          this.isLiked = false;
          this.likeStateChanged.emit();
        },
      });
    }
  }
}
